import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { Button } from 'primeng/button';
import { SelectButton } from 'primeng/selectbutton';
import { Toast } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { CmsCardComponent } from '../cms-card/cms-card';
import { CmsApiService, CmsItem } from '../cms-api/cms-api.service';
const ALL_VALUE = '__all__';
type CardProduct = CmsItem & { _cardId: number };
@Component({
  selector: 'lib-cms-products',
  standalone: true,
  imports: [CmsCardComponent, Button, SelectButton, Toast, FormsModule, CdkDropList, CdkDrag],
  providers: [MessageService],
  templateUrl: './cms-products.html',
  styleUrl: './cms-products.scss'
})
export class CmsProductsComponent implements OnInit {
  private readonly api = inject(CmsApiService);
  private readonly messageService = inject(MessageService);
  private nextId = 0;
  readonly products = signal<CardProduct[]>([]);
  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  readonly allLabels = computed<string[]>(() => {
    const set = new Set<string>();
    this.products().forEach((p: CardProduct) => p.labels.forEach((l: string) => set.add(l)));
    return Array.from(set).sort();
  });
  readonly labelOptions = computed<{ label: string; value: string }[]>(() => [
    { label: 'All', value: ALL_VALUE },
    ...this.allLabels().map((l: string) => ({ label: l, value: l }))
  ]);
  readonly selectedLabels = signal<string[]>([]);
  readonly displayedSelection = computed<string[]>(() =>
    this.selectedLabels().length === 0 ? [ALL_VALUE] : this.selectedLabels()
  );
  readonly filteredProducts = computed<CardProduct[]>(() => {
    const selected = this.selectedLabels();
    if (!selected.length) return this.products();
    return this.products().filter((p: CardProduct) => p.labels.some((l: string) => selected.includes(l)));
  });
  readonly hasInvalidCards = computed<boolean>(() =>
    this.products().some((p: CardProduct) => !p.title.trim() || !p.description.trim() || !p.image || p.labels.length === 0)
  );

  ngOnInit(): void {
    this.api.getProducts().subscribe({
      next: (items: CmsItem[]) => {
        this.products.set(items.map((item: CmsItem) => ({ ...item, _cardId: this.nextId++ })));
        this.loading.set(false);
      },
      error: (err: unknown) => {
        console.error('[CmsProducts] Failed to load', err);
        this.error.set('Failed to load products from server.');
        this.loading.set(false);
      }
    });
  }

  onAddProduct(): void {
    this.products.update((list: CardProduct[]) => [
      { title: '', description: '', image: '', labels: [], _cardId: this.nextId++ },
      ...list
    ]);
  }

  onProductChange(cardId: number, updated: CmsItem): void {
    this.products.update((list: CardProduct[]) =>
      list.map((item: CardProduct) => item._cardId === cardId ? { ...updated, _cardId: item._cardId } : item)
    );
  }

  onDeleteProduct(cardId: number): void {
    const item = this.products().find((p: CardProduct) => p._cardId === cardId);
    if (item?.id != null) {
      this.api.deleteProduct(item.id).subscribe({
        error: (err: unknown) => console.error('[CmsProducts] Delete failed', err)
      });
    }
    this.products.update((list: CardProduct[]) => list.filter((p: CardProduct) => p._cardId !== cardId));
  }

  onDrop(event: CdkDragDrop<CardProduct[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    const visibleIds = this.filteredProducts().map((item: CardProduct) => item._cardId);
    this.products.update((list: CardProduct[]) => {
      if (visibleIds.length === list.length) {
        const next = [...list];
        moveItemInArray(next, event.previousIndex, event.currentIndex);
        return next;
      }
      const reorderedVisible = list.filter((item: CardProduct) => visibleIds.includes(item._cardId));
      moveItemInArray(reorderedVisible, event.previousIndex, event.currentIndex);
      const queue = [...reorderedVisible];
      return list.map((item: CardProduct) => visibleIds.includes(item._cardId) ? (queue.shift() ?? item) : item);
    });
  }

  onLabelFilterChange(values: string[] | string | null | undefined): void {
    const vals: string[] = Array.isArray(values) ? values : values ? [values] : [];
    if (!vals.length) { this.selectedLabels.set([]); return; }
    if (vals.includes(ALL_VALUE) && this.selectedLabels().length > 0) { this.selectedLabels.set([]); return; }
    const real = vals.filter((v: string) => v !== ALL_VALUE);
    this.selectedLabels.set(real.length ? real : []);
  }

  onPublish(): void {
    if (this.hasInvalidCards()) return;
    this.saving.set(true);
    const payload = this.products().map(({ _cardId, ...rest }: CardProduct) => rest);
    this.api.publishProducts(payload).subscribe({
      next: (saved: CmsItem[]) => {
        this.products.set(saved.map((item: CmsItem) => ({ ...item, _cardId: this.nextId++ })));
        this.saving.set(false);
        this.messageService.add({ severity: 'success', summary: 'Published', detail: 'Products published successfully.' });
      },
      error: (err: unknown) => {
        console.error('[CmsProducts] Publish failed', err);
        this.saving.set(false);
        this.messageService.add({ severity: 'error', summary: 'Publish failed', detail: 'Could not publish products. Please try again.' });
      }
    });
  }
}
